/** Future media authoring only. Existing visual endpoints, SQL kind checks,
 * candidate review states and audio playback are deliberately unchanged. */
import { canonicalJson, contractError, freezeJson, validateJsonObject, validateSha256, type JsonObject } from '@djgg/gameplay-kit';
import type { CreationAsset } from './types.js';
import type { CreationMediaKindV1 } from './runtimeContract.js';

export const MEDIA_CONTRACT_VERSION = 1;
export const MEDIA_FORMAT_CATALOG_V1 = freezeJson({
  image: ['image/png', 'image/jpeg', 'image/webp'],
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg'],
  palette: ['application/json'],
});
interface MediaMetaBaseV1 { sha256: string; mime: string; bytes: number; }
export type MediaMetaV1 =
  | (MediaMetaBaseV1 & { type: 'image'; width: number; height: number })
  | (MediaMetaBaseV1 & { type: 'palette'; colors: string[] })
  | (MediaMetaBaseV1 & { type: 'audio'; durationMs: number; sampleRate: number; channels: 1 | 2;
      loudnessLufs?: number; loopStartMs?: number; loopEndMs?: number });
export interface MediaProvenanceV1 {
  source: 'curated' | 'upload' | 'generated';
  reference: string;
  license: { source: string; termsVersion: string; attribution?: string };
  generator?: { provider: string; model: string; requestId?: string };
}
export interface CreationMediaAssetV1 {
  schemaVersion: 1;
  name: string;
  variantId: string;
  kind: CreationMediaKindV1;
  meta: MediaMetaV1;
  provenance: MediaProvenanceV1;
  brief: string;
  actorUid: string;
  actorHandle: string;
  createdAtMs: number;
}
/** Read-model states: changing working-head attachment never implies live. */
export type MediaLifecycleV1 =
  | { state: 'candidate'; creationId: string; review: 'pending' | 'attached' | 'skipped' | 'superseded' | 'rejected' }
  | { state: 'working-head'; creationId: string }
  | { state: 'contained-in-version' | 'active-live'; creationId: string; version: number };
export interface AssetRejectionV1 {
  schemaVersion: 1;
  creationId: string;
  contentSha256: string;
  scope: 'creation';
  reason: 'creator-rejected' | 'wrong-role' | 'quality' | 'license';
  note?: string;
  variantId?: string;
  name?: string;
  actorUid: string;
  actorHandle: string;
  rejectedAtMs: number;
  sourceAction: 'candidate-review' | 'working-head-review';
  replacementVariantId?: string;
  /** Explicit audit data for undo; the original rejection identity remains. */
  undo?: { actorUid: string; atMs: number };
}

function keys(v: JsonObject, allowed: readonly string[], path: string): void {
  for (const key of Object.keys(v)) if (!allowed.includes(key)) contractError(`${path}.${key}`, 'unsupported field');
}
function text(v: unknown, path: string, max = 160): string {
  if (typeof v !== 'string' || !v.trim() || v.length > max) contractError(path, `expected nonempty text up to ${max} characters`);
  return v;
}
function integer(v: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(v) || (v as number) < min || (v as number) > max) contractError(path, `expected integer in [${min}, ${max}]`);
  return v as number;
}
function identity(v: unknown, path: string): string {
  const id = text(v, path, 128);
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(id)) contractError(path, 'expected path-safe identity');
  return id;
}

/** Metadata validation is not decode, magic-byte, loudness or license proof.
 * Ingestion must perform those checks on exact bytes before Phase 4 writers. */
export function validateMediaMetaV1(value: unknown): MediaMetaV1 {
  const v = validateJsonObject(value, 'media.meta');
  const type = v.type;
  if (type !== 'image' && type !== 'audio' && type !== 'palette') contractError('media.meta.type', 'unsupported media type');
  const common = ['type', 'sha256', 'mime', 'bytes'];
  keys(v, [...common, ...(type === 'image' ? ['width', 'height'] : type === 'palette' ? ['colors'] : ['durationMs', 'sampleRate', 'channels', 'loudnessLufs', 'loopStartMs', 'loopEndMs'])], 'media.meta');
  if (typeof v.mime !== 'string' || !MEDIA_FORMAT_CATALOG_V1[type].includes(v.mime)) contractError('media.meta.mime', `supported: ${MEDIA_FORMAT_CATALOG_V1[type].join(', ')}`);
  const base = { sha256: validateSha256(v.sha256), mime: v.mime, bytes: integer(v.bytes, 'media.meta.bytes', 1, type === 'palette' ? 65536 : 20 * 1024 * 1024) };
  if (type === 'image') return { ...base, type, width: integer(v.width, 'media.meta.width', 1, 16384), height: integer(v.height, 'media.meta.height', 1, 16384) };
  if (type === 'palette') {
    if (!Array.isArray(v.colors) || !v.colors.length || v.colors.length > 32 || !v.colors.every(c => typeof c === 'string' && /^#[a-f0-9]{6}$/.test(c))) contractError('media.meta.colors', 'expected 1..32 lowercase #rrggbb colors');
    return { ...base, type, colors: v.colors as string[] };
  }
  const audio: Extract<MediaMetaV1, { type: 'audio' }> = { ...base, type,
    durationMs: integer(v.durationMs, 'media.meta.durationMs', 1, 600000), sampleRate: integer(v.sampleRate, 'media.meta.sampleRate', 8000, 96000),
    channels: integer(v.channels, 'media.meta.channels', 1, 2) as 1 | 2 };
  if (v.loudnessLufs !== undefined) {
    if (typeof v.loudnessLufs !== 'number' || v.loudnessLufs < -100 || v.loudnessLufs > 0) contractError('media.meta.loudnessLufs', 'expected finite loudness in [-100, 0]');
    audio.loudnessLufs = v.loudnessLufs;
  }
  if ((v.loopStartMs === undefined) !== (v.loopEndMs === undefined)) contractError('media.meta', 'both loop points must be supplied together');
  if (v.loopStartMs !== undefined) {
    audio.loopStartMs = integer(v.loopStartMs, 'media.meta.loopStartMs', 0, audio.durationMs - 1);
    audio.loopEndMs = integer(v.loopEndMs, 'media.meta.loopEndMs', audio.loopStartMs + 1, audio.durationMs);
  }
  return audio;
}

export function validateCreationMediaAssetV1(value: unknown): CreationMediaAssetV1 {
  const v = validateJsonObject(value, 'media');
  keys(v, ['schemaVersion', 'name', 'variantId', 'kind', 'meta', 'provenance', 'brief', 'actorUid', 'actorHandle', 'createdAtMs'], 'media');
  if (v.schemaVersion !== MEDIA_CONTRACT_VERSION) contractError('media.schemaVersion', 'unsupported version; supported: 1');
  const kinds = ['sprite', 'background', 'icon', 'palette', 'cover', 'strip', 'music', 'sfx'];
  if (typeof v.kind !== 'string' || !kinds.includes(v.kind)) contractError('media.kind', `supported: ${kinds.join(', ')}`);
  const meta = validateMediaMetaV1(v.meta);
  const expected = v.kind === 'music' || v.kind === 'sfx' ? 'audio' : v.kind === 'palette' ? 'palette' : 'image';
  if (meta.type !== expected) contractError('media.meta.type', `${v.kind} requires ${expected} metadata`);
  if (v.kind === 'sfx' && meta.type === 'audio' && (meta.durationMs > 10000 || meta.loopStartMs !== undefined)) contractError('media.meta', 'SFX must be at most 10 seconds and cannot loop');
  const p = validateJsonObject(v.provenance, 'media.provenance');
  keys(p, ['source', 'reference', 'license', 'generator'], 'media.provenance');
  if (!['curated', 'upload', 'generated'].includes(p.source as string)) contractError('media.provenance.source', 'expected curated, upload or generated');
  const l = validateJsonObject(p.license, 'media.provenance.license');
  keys(l, ['source', 'termsVersion', 'attribution'], 'media.provenance.license');
  const provenance: MediaProvenanceV1 = { source: p.source as MediaProvenanceV1['source'], reference: text(p.reference, 'media.provenance.reference', 2048),
    license: { source: text(l.source, 'media.license.source', 2048), termsVersion: text(l.termsVersion, 'media.license.termsVersion'),
      ...(l.attribution === undefined ? {} : { attribution: text(l.attribution, 'media.license.attribution', 1000) }) } };
  if (p.source === 'generated') {
    const g = validateJsonObject(p.generator, 'media.provenance.generator'); keys(g, ['provider', 'model', 'requestId'], 'media.provenance.generator');
    provenance.generator = { provider: text(g.provider, 'media.generator.provider'), model: text(g.model, 'media.generator.model'),
      ...(g.requestId === undefined ? {} : { requestId: text(g.requestId, 'media.generator.requestId') }) };
  } else if (p.generator !== undefined) contractError('media.provenance.generator', 'only generated provenance has generator metadata');
  return { schemaVersion: 1, kind: v.kind as CreationMediaKindV1, name: identity(v.name, 'media.name'), variantId: identity(v.variantId, 'media.variantId'),
    meta, provenance, brief: text(v.brief, 'media.brief', 2000), actorUid: text(v.actorUid, 'media.actorUid', 128),
    actorHandle: text(v.actorHandle, 'media.actorHandle'), createdAtMs: integer(v.createdAtMs, 'media.createdAtMs', 0) };
}

export function validateMediaLifecycleV1(value: unknown): MediaLifecycleV1 {
  const v = validateJsonObject(value, 'media.lifecycle');
  const creationId = identity(v.creationId, 'media.lifecycle.creationId');
  if (v.state === 'working-head') { keys(v, ['state', 'creationId'], 'media.lifecycle'); return { state: v.state, creationId }; }
  if (v.state === 'contained-in-version' || v.state === 'active-live') {
    keys(v, ['state', 'creationId', 'version'], 'media.lifecycle'); return { state: v.state, creationId, version: integer(v.version, 'media.lifecycle.version', 1) };
  }
  if (v.state === 'candidate') {
    keys(v, ['state', 'creationId', 'review'], 'media.lifecycle');
    if (!['pending', 'attached', 'skipped', 'superseded', 'rejected'].includes(v.review as string)) contractError('media.lifecycle.review', 'unsupported review state');
    return { state: v.state, creationId, review: v.review as Extract<MediaLifecycleV1, { state: 'candidate' }>['review'] };
  }
  contractError('media.lifecycle.state', 'expected candidate, working-head, contained-in-version or active-live');
}

export function validateAssetRejectionV1(value: unknown): AssetRejectionV1 {
  const v = validateJsonObject(value, 'assetRejection');
  keys(v, ['schemaVersion', 'creationId', 'contentSha256', 'scope', 'reason', 'note', 'variantId', 'name', 'actorUid', 'actorHandle', 'rejectedAtMs', 'sourceAction', 'replacementVariantId', 'undo'], 'assetRejection');
  if (v.schemaVersion !== 1) contractError('assetRejection.schemaVersion', 'unsupported version; supported: 1');
  if (v.scope !== 'creation') contractError('assetRejection.scope', 'only creation-scoped exact-byte rejection is supported');
  if (!['creator-rejected', 'wrong-role', 'quality', 'license'].includes(v.reason as string)) contractError('assetRejection.reason', 'unsupported reason');
  if (v.sourceAction !== 'candidate-review' && v.sourceAction !== 'working-head-review') contractError('assetRejection.sourceAction', 'unsupported source action');
  const r: AssetRejectionV1 = { schemaVersion: 1, creationId: identity(v.creationId, 'assetRejection.creationId'), contentSha256: validateSha256(v.contentSha256),
    scope: 'creation', reason: v.reason as AssetRejectionV1['reason'], actorUid: text(v.actorUid, 'assetRejection.actorUid', 128), actorHandle: text(v.actorHandle, 'assetRejection.actorHandle'),
    rejectedAtMs: integer(v.rejectedAtMs, 'assetRejection.rejectedAtMs', 0), sourceAction: v.sourceAction };
  for (const k of ['variantId', 'name', 'replacementVariantId'] as const) if (v[k] !== undefined) r[k] = identity(v[k], `assetRejection.${k}`);
  if (v.note !== undefined) r.note = text(v.note, 'assetRejection.note', 1000);
  if (v.undo !== undefined) { const undo = validateJsonObject(v.undo); keys(undo, ['actorUid', 'atMs'], 'assetRejection.undo'); r.undo = { actorUid: text(undo.actorUid, 'assetRejection.undo.actorUid', 128), atMs: integer(undo.atMs, 'assetRejection.undo.atMs', r.rejectedAtMs) }; }
  return r;
}

/** Pure identity helper only; no rejection store, attachment or charging path. */
export function assetRejectionIdentity(value: Pick<AssetRejectionV1, 'creationId' | 'contentSha256'>): string {
  return canonicalJson([identity(value.creationId, 'creationId'), validateSha256(value.contentSha256)]);
}

/** Legacy rows retain their original value and acquire no inferred digest. */
export function readLegacyOrMediaAsset(value: CreationAsset | CreationMediaAssetV1):
  { contract: 'legacy'; asset: CreationAsset } | { contract: 1; asset: CreationMediaAssetV1 } {
  if (!Object.hasOwn(value, 'schemaVersion')) return { contract: 'legacy', asset: value as CreationAsset };
  return { contract: 1, asset: validateCreationMediaAssetV1(value) };
}
